import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    History as HistoryIcon,
    ShieldCheck,
    Calendar,
    FileText,
    AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const RevocationHistoryModal = ({ isOpen, onClose, revokedPunishments = [] }) => {
    if (!isOpen) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="bg-[#0f1115] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden relative shadow-2xl z-10 flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-white/5 flex items-center justify-between bg-gradient-to-r from-green-500/5 to-emerald-500/5">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                                    <HistoryIcon className="h-6 w-6 text-green-400" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Histórico de Revogações</h2>
                                    <p className="text-sm text-gray-400">Registro de punições anuladas</p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="text-gray-400 hover:text-white hover:bg-white/5 rounded-full"
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-4">
                            {revokedPunishments.length > 0 ? (
                                revokedPunishments.map((punishment) => (
                                    <div
                                        key={punishment.id}
                                        className="bg-white/5 border border-green-500/20 rounded-xl p-4 space-y-3 relative overflow-hidden"
                                    >
                                        <div className="absolute top-0 right-0 p-2 opacity-10">
                                            <ShieldCheck className="h-24 w-24 text-green-500" />
                                        </div>

                                        <div className="flex items-center justify-between relative z-10">
                                            <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                                                {punishment.punishmentType}
                                            </Badge>
                                            <span className="text-xs text-green-500/80 font-mono">
                                                {formatDate(punishment.revokedAt)}
                                            </span>
                                        </div>

                                        <div className="space-y-3 relative z-10">
                                            <div className="bg-[#0a0c10]/50 p-3 rounded-lg border border-white/5">
                                                <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-bold">Motivo da Revogação</p>
                                                <p className="text-gray-300 text-sm">
                                                    {punishment.revocationReason || 'Nenhum motivo registrado.'}
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                                                <div className="flex items-center gap-2">
                                                    <ShieldCheck className="h-3 w-3 text-green-500" />
                                                    <span>Revogado por: <span className="text-white font-medium">{punishment.revokedBy}</span></span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-3 w-3" />
                                                    <span>Aplicada em: {new Date(punishment.appliedAt).toLocaleDateString()}</span>
                                                </div>
                                            </div>

                                            {/* Original Reason Accordion/Snippet */}
                                            <div className="pt-2 border-t border-white/5">
                                                <div className="flex items-start gap-2">
                                                    <AlertCircle className="h-4 w-4 text-gray-500 mt-0.5" />
                                                    <div>
                                                        <span className="text-xs text-gray-500 font-medium">Motivo original:</span>
                                                        <p className="text-xs text-gray-400 line-clamp-2 italic">"{punishment.publicReason}"</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
                                    <div className="p-4 bg-white/5 rounded-full mb-4">
                                        <ShieldCheck className="h-12 w-12 opacity-50" />
                                    </div>
                                    <p className="text-lg font-medium text-white mb-1">Nenhuma revogação</p>
                                    <p className="text-sm max-w-xs mx-auto">Não há registros de punições revogadas para este personagem.</p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-white/5 bg-[#0f1115] flex justify-end">
                            <Button
                                onClick={onClose}
                                className="bg-white/5 hover:bg-white/10 text-white border border-white/10"
                            >
                                Fechar
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default RevocationHistoryModal;
